import React, { useEffect, useState } from 'react';
import { bool, arrayOf, oneOfType } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  REVIEW_TYPE_OF_PROVIDER,
  REVIEW_TYPE_OF_CUSTOMER,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_YOUTUBE,
  propTypes,
  isCreatorType,
  isAthleteType,
} from '../../util/types';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  PROFILE_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
  isNotFoundError,
} from '../../util/errors';
import { pickCustomFieldProps } from '../../util/fieldHelpers';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  Heading,
  H2,
  H4,
  Page,
  AvatarLarge,
  NamedLink,
  ListingCard,
  Reviews,
  ButtonTabNavHorizontal,
  LayoutSideNavigation,
  NamedRedirect,
  IconSpinner,
  GamificationBadge,
  DraftContent,
  Logo,
  IconHudl,
  IconMail,
  IconSocialMediaInstagram
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';

import css from './ProfilePage.module.css';
import SectionDetailsMaybe from './SectionDetailsMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionMultiEnumMaybe from './SectionMultiEnumMaybe';
import SectionYoutubeVideoMaybe from './SectionYoutubeVideoMaybe';

import SectionMedia from './SectionMedia';

const MAX_MOBILE_SCREEN_WIDTH = 768;
const MIN_LENGTH_FOR_LONG_WORDS = 20;

export const AsideContent = props => {
  const {
    user,
    displayName,
    showLinkToProfileSettingsPage,
    badgeName,
    isAthlete,
    athleteInfo,
    athleteNumber,
    profileEmail,
    instagramUrl,
    hudlUrl,
  } = props;

  return (
    <div className={css.asideContent}>
      <AvatarLarge className={css.avatar} user={user} disableProfileLink />
      <H2 as="h1" className={css.mobileHeading}>
        {displayName ? (
          <FormattedMessage
            id={isAthlete ? "ProfilePage.headingAthlete" : "ProfilePage.mobileHeading"}
            values={{ name: displayName, number: athleteNumber ? <b>#{athleteNumber}</b> : null }}
          />
        ) : null}
        {!isAthlete && (
          <GamificationBadge
            titleClassName={css.titleBadge}
            itemClassName={css.mobileProfileItem}
            className={css.mobileBadge}
            badgeName={badgeName}
          />
        )}
      </H2>
      <div className={css.athleteInfoMobile}>
        {athleteInfo}
      </div>
      {isAthlete && (
        <div className={css.athleteButtons}>
          {instagramUrl && (
            <a className={css.athleteButton} href={instagramUrl}>
              <IconSocialMediaInstagram />
            </a>
          )}
          {/* <a className={css.athleteButton} onClick={() => alert("Is under development!")}>
            <FormattedMessage id="ProfilePage.transcript" />
          </a> */}
          {profileEmail && (
            <a className={css.athleteButton} href={`mailto:${profileEmail}`}>
              <IconMail />
              <FormattedMessage id="ProfilePage.sendEmail" />
            </a>
          )}
          {hudlUrl && (
            <a className={css.athleteButton} href={hudlUrl}>
              <IconHudl />
              <FormattedMessage id="ProfilePage.hudl" />
            </a>
          )}
        </div>
      )}
      {showLinkToProfileSettingsPage ? (
        <>
          <NamedLink className={css.editLinkMobile} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkMobile" />
          </NamedLink>
          <NamedLink className={css.editLinkDesktop} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
          </NamedLink>
        </>
      ) : null}
    </div>
  );
};

export const ReviewsErrorMaybe = props => {
  const { queryReviewsError } = props;
  return queryReviewsError ? (
    <p className={css.error}>
      <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
    </p>
  ) : null;
};

export const MobileReviews = props => {
  const { reviews, queryReviewsError, isAthlete, isCreator } = props;
  const reviewsOfProvider = reviews?.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews?.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);

  return (
    <div className={css.mobileReviews}>
      {isCreator && (
        <>
          <H4 as="h2" className={css.mobileReviewsTitle}>
            <FormattedMessage
              id="ProfilePage.reviewsFromMyCustomersTitle"
              values={{ count: reviewsOfProvider?.length || 0 }}
            />
          </H4>
          <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
          <Reviews reviews={reviewsOfProvider} />
        </>
      )}

      {isAthlete && (
        <>
          <H4 as="h2" className={css.mobileReviewsTitle}>
            <FormattedMessage
              id="ProfilePage.reviewsAsACustomerTitle"
              values={{ count: reviewsOfCustomer?.length || 0 }}
            />
          </H4>
          <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
          <Reviews reviews={reviewsOfCustomer} />
        </>
      )}
    </div>
  );
};

export const DesktopReviews = props => {
  const [showReviewsType, setShowReviewsType] = useState(REVIEW_TYPE_OF_PROVIDER);
  const { reviews, queryReviewsError, isCreator, isAthlete } = props;
  const reviewsOfProvider = reviews?.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews?.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  const isReviewTypeProviderSelected = showReviewsType === REVIEW_TYPE_OF_PROVIDER;
  const isReviewTypeCustomerSelected = showReviewsType === REVIEW_TYPE_OF_CUSTOMER;

  let desktopReviewTabs = isCreator
    ? [
      {
        text: (
          <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
            <FormattedMessage
              id="ProfilePage.reviewsFromMyCustomersTitle"
              values={{ count: reviewsOfProvider?.length || 0 }}
            />
          </Heading>
        ),
        selected: true,
        onClick: () => setShowReviewsType(REVIEW_TYPE_OF_PROVIDER),
        id: REVIEW_TYPE_OF_PROVIDER,
      },
    ]
    : [
      {
        text: (
          <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
            <FormattedMessage
              id="ProfilePage.reviewsAsACustomerTitle"
              values={{ count: reviewsOfCustomer?.length || 0 }}
            />
          </Heading>
        ),
        selected: true,
        onClick: () => setShowReviewsType(REVIEW_TYPE_OF_CUSTOMER),
        id: REVIEW_TYPE_OF_CUSTOMER,
      },
    ];

  return (
    <div className={css.desktopReviews}>
      <div className={css.desktopReviewsWrapper}>
        <ButtonTabNavHorizontal className={css.desktopReviewsTabNav} tabs={desktopReviewTabs} />

        <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />

        {isCreator ? (
          <Reviews reviews={reviewsOfProvider} />
        ) : (
          <Reviews reviews={reviewsOfCustomer} isReviewsOfCustomer={true} />
        )}
      </div>
    </div>
  );
};

export const CustomUserFields = props => {
  const { publicData, metadata, userFieldConfig } = props;

  const shouldPickUserField = fieldConfig => fieldConfig?.showConfig?.displayInProfile !== false;
  const propsForCustomFields =
    pickCustomFieldProps(publicData, metadata, userFieldConfig, 'userType', shouldPickUserField) ||
    [];

  return (
    <>
      <SectionDetailsMaybe {...props} />
      {propsForCustomFields.map(customFieldProps => {
        const { schemaType, ...fieldProps } = customFieldProps;
        return schemaType === SCHEMA_TYPE_MULTI_ENUM ? (
          <SectionMultiEnumMaybe {...fieldProps} />
        ) : schemaType === SCHEMA_TYPE_TEXT ? (
          <SectionTextMaybe {...fieldProps} />
        ) : schemaType === SCHEMA_TYPE_YOUTUBE ? (
          <SectionYoutubeVideoMaybe {...fieldProps} />
        ) : null;
      })}
    </>
  );
};

export const MainContent = props => {
  const {
    userShowError,
    bio,
    displayName,
    listings,
    queryListingsError,
    reviews,
    queryReviewsError,
    publicData,
    metadata,
    userFieldConfig,
    intl,
    hideReviews,
    profileUser,
    onManageDisableScrolling,
    showMediaSections,
    currentUser,
    isCreator,
    isAthlete,
    isCurrentUserAthlete,
    athleteInfo,
    athleteParams,
    achievements,
    athleteNumber,
  } = props;

  const hasListings = listings.length > 0;
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;

  const hasBio = (() => {
    if (!bio || typeof bio !== 'string') return false;

    try {
      const parsed = JSON.parse(bio);

      if (!parsed.blocks || !Array.isArray(parsed.blocks)) return false;

      const hasText = parsed.blocks.some(
        block => block.text && block.text.trim().length > 0
      );

      return hasText;
    } catch {
      return bio.trim().length > 0;
    }
  })();

  const listingsContainerClasses = classNames(css.listingsContainer, {
    [css.withBioMissingAbove]: !hasBio,
  });

  if (userShowError || queryListingsError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingDataFailed" />
      </p>
    );
  }

  const badgeName = profileUser?.attributes?.profile?.publicData?.badgeName;
  const gameHighlights = profileUser?.attributes?.profile?.publicData?.gameHighlights || [];
  const gameHighlightsVideos =
    profileUser?.attributes?.profile?.publicData?.gameHighlightsVideos || [];
  const practiceClips = profileUser?.attributes?.profile?.publicData?.practiceClips || [];
  const practiceClipsVideos =
    profileUser?.attributes?.profile?.publicData?.practiceClipsVideos || [];

  let userFieldConfigFormated = userFieldConfig;
  if (isAthlete) {
    userFieldConfigFormated = [];
  }

  return (
    <div>
      <H2 as="h1" className={css.desktopHeading}>
        {isAthlete ? (
          <FormattedMessage
            id="ProfilePage.headingAthlete"
            values={{ name: displayName, number: athleteNumber ? <b>#{athleteNumber}</b> : null }}
          />
        ) : (
          <FormattedMessage
            id="ProfilePage.desktopHeading"
            values={{ name: displayName }}
          />
        )}
        {!isAthlete && <GamificationBadge itemClassName={css.profileItem} badgeName={badgeName} />}
      </H2>

      {athleteInfo}
      {athleteParams}

      {hasBio ? (
        <div className={css.bio}>
          {isAthlete && <h3 className={css.bioHeading}>Bio</h3>}
          <DraftContent draftContent={bio} />
        </div>
      ) : null}

      {!!achievements?.length && (
        <div className={css.achievements}>
          <h3 className={css.achievementsHeading}>Achievements & Stats</h3>
          <ul className={css.achievementsList}>
            {achievements.map((a, i) => {
              const name = a.name;
              if (typeof name !== 'string' || name.trim().length === 0) {
                return null;
              }
              return (
                <li key={`achievement-${i}`}>{name.trim()}</li>
              );
            })}
          </ul>
        </div>
      )}

      {displayName ? (
        <CustomUserFields
          publicData={publicData}
          metadata={metadata}
          userFieldConfig={userFieldConfigFormated}
          intl={intl}
        />
      ) : null}

      {showMediaSections && (
        <>
          <SectionMedia
            key="gameHighlights"
            media={gameHighlights}
            mediaName="gameHighlights"
            onManageDisableScrolling={onManageDisableScrolling}
          />
          <SectionMedia
            key="gameHighlightsVideos"
            media={gameHighlightsVideos}
            mediaName="gameHighlightsVideos"
            onManageDisableScrolling={onManageDisableScrolling}
          />
          <SectionMedia
            key="practiceClips"
            media={practiceClips}
            mediaName="practiceClips"
            onManageDisableScrolling={onManageDisableScrolling}
          />
          <SectionMedia
            key="practiceClipsVideos"
            media={practiceClipsVideos}
            mediaName="practiceClipsVideos"
            onManageDisableScrolling={onManageDisableScrolling}
          />
        </>
      )}

      {(!!gameHighlights?.length ||
        !!practiceClips?.length ||
        !!gameHighlightsVideos?.length ||
        !!practiceClipsVideos.length) &&
        !showMediaSections && (
          <div className={css.sectionLoader}>
            <IconSpinner />
          </div>
        )}

      {hasListings ? (
        <div className={listingsContainerClasses}>
          <H4 as="h2" className={css.listingsTitle}>
            <FormattedMessage id="ProfilePage.listingsTitle" values={{ count: listings.length }} />
          </H4>
          <ul className={css.listings}>
            {listings.map(l => (
              <li className={css.listing} key={l.id.uuid}>
                <ListingCard
                  onManageDisableScrolling={onManageDisableScrolling}
                  isAthlete={isCurrentUserAthlete}
                  listing={l}
                  showAuthorInfo={false}
                />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {hideReviews ? null : isMobileLayout ? (
        <MobileReviews
          reviews={reviews}
          queryReviewsError={queryReviewsError}
          isCreator={isCreator}
          isAthlete={isAthlete}
        />
      ) : (
        <DesktopReviews
          reviews={reviews}
          queryReviewsError={queryReviewsError}
          isCreator={isCreator}
          isAthlete={isAthlete}
        />
      )}
    </div>
  );
};

export const ProfilePageComponent = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const [mounted, setMounted] = useState(false);
  const [showMediaSections, setShowMediaSections] = useState(false);

  useEffect(() => {
    setMounted(true);
    const timer = setTimeout(() => {
      setShowMediaSections(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const {
    scrollingDisabled,
    params: pathParams,
    currentUser,
    useCurrentUser,
    userShowError,
    user,
    reviews,
    ...rest
  } = props;
  const isVariant = pathParams.variant?.length > 0;
  const isPreview = isVariant && pathParams.variant === PROFILE_PAGE_PENDING_APPROVAL_VARIANT;

  // Stripe's onboarding needs a business URL for each seller, but the profile page can be
  // too empty for the provider at the time they are creating their first listing.
  // To remedy the situation, we redirect Stripe's crawler to the landing page of the marketplace.
  // TODO: When there's more content on the profile page, we should consider by-passing this redirection.
  const searchParams = rest?.location?.search;
  const isStorefront = searchParams
    ? new URLSearchParams(searchParams)?.get('mode') === 'storefront'
    : false;
  if (isStorefront) {
    return <NamedRedirect name="LandingPage" />;
  }

  const isCurrentUser = currentUser?.id && currentUser?.id?.uuid === pathParams.id;
  const profileUser = useCurrentUser ? currentUser : user;
  const { bio, displayName, publicData, metadata } = profileUser?.attributes?.profile || {};
  const { userFields } = config.user;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const isUnauthorizedOnPrivateMarketplace = isPrivateMarketplace && isUnauthorizedUser;
  const hasUserPendingApprovalError = isErrorUserPendingApproval(userShowError);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasNoViewingRightsOnPrivateMarketplace = isPrivateMarketplace && hasNoViewingRightsUser;
  const badgeName = profileUser?.attributes?.profile?.publicData?.badgeName;
  const athleteBannerImg = profileUser?.attributes?.profile?.publicData?.athleteBanner?.[0];

  const isDataLoaded = isPreview
    ? currentUser != null || userShowError != null
    : hasNoViewingRightsOnPrivateMarketplace
      ? currentUser != null || userShowError != null
      : user != null || userShowError != null;

  const schemaTitleVars = { name: displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);

  const isCreator = isCreatorType(profileUser);
  const isAthlete = isAthleteType(profileUser);
  const isCurrentUserAthlete = isAthleteType(currentUser);

  const schoolName = publicData?.Schoolname;
  const clubName = publicData?.Clubname;
  const lastSchoolYear = publicData?.Lastschoolyear;
  const lastSchoolYearOptions = userFields?.find(f => f.key === "Lastschoolyear").enumOptions;
  const lastSchoolYearLabel = lastSchoolYearOptions?.find(y => y.option === lastSchoolYear)?.label;
  const gpa = publicData?.GPA;

  const feet = publicData?.feet;
  const inches = publicData?.inches;
  const weight = publicData?.Weight;
  const position = publicData?.Position;

  const achievements = publicData?.achievements;
  const athleteNumber = publicData?.Athletenumber;

  const profileEmail = publicData?.profileEmail;
  const instagramUrl = publicData?.instagramUrl;
  const hudlUrl = publicData?.hudlUrl;

  const athleteInfo = isAthlete && (clubName || schoolName || lastSchoolYearLabel || gpa) && (
    <div className={css.athleteInfo}>
      <p className={css.athleteSchoolInfo}>
        {clubName && <span>{clubName}</span>}
        {schoolName && <span>{schoolName}</span>}
        {lastSchoolYearLabel && <span>{lastSchoolYearLabel}</span>}
        {gpa && <span>GPA {gpa}</span>}
      </p>
    </div>
  );
  const athleteParams = isAthlete && (
    <div className={css.athleteParamsInfo}>
      {feet && inches && (
        <p className={css.athleteParamsInfoItem}>
          <b>{feet}'{inches}"</b>Height
        </p>
      )}
      {weight && (
        <p className={css.athleteParamsInfoItem}>
          <b>{weight}</b>Weight (lbs)
        </p>
      )}
      {position && (
        <p className={css.athleteParamsInfoItem}>
          <b>{position}</b>Position
        </p>
      )}
    </div>
  );

  const banner = (
    <div className={css.bannerImage}>
      {!!athleteBannerImg ? <img src={athleteBannerImg.location} alt="Athlete Banner" /> : <Logo />}
    </div>
  );

  if (!isDataLoaded) {
    return null;
  } else if (!isPreview && isNotFoundError(userShowError)) {
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (!isPreview && (isUnauthorizedOnPrivateMarketplace || hasUserPendingApprovalError)) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (!isPreview && hasNoViewingRightsOnPrivateMarketplace && !isCurrentUser) ||
    isErrorNoViewingPermission(userShowError)
  ) {
    // Someone without viewing rights on a private marketplace is trying to
    // view a profile page that is not their own – redirect to NoAccessPage
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  } else if (!isPreview && isForbiddenError(userShowError)) {
    // This can happen if private marketplace mode is active, but it's not reflected through asset yet.
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  } else if (isPreview && mounted && !isCurrentUser) {
    // Someone is manipulating the URL, redirect to current user's profile page.
    return isCurrentUser === false ? (
      <NamedRedirect name="ProfilePage" params={{ id: currentUser?.id?.uuid }} />
    ) : null;
  } else if ((isPreview || isPrivateMarketplace) && !mounted) {
    // This preview of the profile page is not rendered on server-side
    // and the first pass on client-side should render the same UI.
    return null;
  }
  // This is rendering normal profile page (not preview for pending-approval)
  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        name: schemaTitle,
      }}
    >
      <LayoutSideNavigation
        sideNavClassName={css.aside}
        mainColumnClassName={css.main}
        topbar={<TopbarContainer />}
        sideNav={
          <AsideContent
            user={profileUser}
            showLinkToProfileSettingsPage={mounted && isCurrentUser}
            displayName={displayName}
            badgeName={badgeName}
            isAthlete={isAthlete}
            athleteInfo={athleteInfo}
            athleteParams={athleteParams}
            achievements={achievements}
            athleteNumber={athleteNumber}
            profileEmail={profileEmail}
            instagramUrl={instagramUrl}
            hudlUrl={hudlUrl}
          />
        }
        footer={<FooterContainer />}
        className={classNames(css.root, { [css.rootAthlete]: isAthlete })}
        containerClassName={classNames(css.container, { [css.containerAthlete]: isAthlete })}
        banner={isAthlete && banner}
        bannerClassName={css.athleteBanner}
      >
        <MainContent
          bio={bio}
          displayName={displayName}
          userShowError={userShowError}
          publicData={publicData}
          metadata={metadata}
          userFieldConfig={userFields}
          hideReviews={hasNoViewingRightsOnPrivateMarketplace || !reviews?.length}
          intl={intl}
          reviews={reviews}
          profileUser={profileUser}
          showMediaSections={showMediaSections}
          currentUser={currentUser}
          isCreator={isCreator}
          isAthlete={isAthlete}
          isCurrentUserAthlete={isCurrentUserAthlete}
          athleteInfo={athleteInfo}
          athleteParams={athleteParams}
          achievements={achievements}
          athleteNumber={athleteNumber}
          {...rest}
        />
      </LayoutSideNavigation>
    </Page>
  );
};

ProfilePageComponent.defaultProps = {
  currentUser: null,
  user: null,
  userShowError: null,
  queryListingsError: null,
  reviews: [],
  queryReviewsError: null,
};

ProfilePageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUser: propTypes.currentUser,
  useCurrentUser: bool.isRequired,
  user: oneOfType([propTypes.user, propTypes.currentUser]),
  userShowError: propTypes.error,
  queryListingsError: propTypes.error,
  listings: arrayOf(oneOfType([propTypes.listing, propTypes.ownListing])).isRequired,
  reviews: arrayOf(propTypes.review),
  queryReviewsError: propTypes.error,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    userListingRefs,
    reviews,
    queryReviewsError,
  } = state.ProfilePage;

  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;

  // Show currentUser's data if it's not approved yet
  const isCurrentUser = userId?.uuid === currentUser?.id?.uuid;
  const useCurrentUser =
    isCurrentUser && !(isUserAuthorized(currentUser) && hasPermissionToViewData(currentUser));

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    useCurrentUser,
    user,
    userShowError,
    queryListingsError,
    listings: getMarketplaceEntities(state, userListingRefs),
    reviews,
    queryReviewsError,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const ProfilePage = compose(connect(mapStateToProps, mapDispatchToProps))(ProfilePageComponent);

export default ProfilePage;
